import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, Eye, Package, Upload, Trash2, ChevronDown, ChevronUp, Image as ImgIcon, Download, Pencil } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  getSalesOrders, createSalesOrder, getSalesOrder, getFirms, getFlowMyCustomers, getFlowMyItems, checkPendingItems,
  downloadSalesOrderPdf, updateSalesOrder, searchStockItems, deleteSalesOrder, getCustomerItemLastPrice,
  adminRevertItem,
} from '../../services/taskApi';
import { fileUrl } from '../../utils/fileUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  'Approved': 'bg-green-500/15 text-green-400',
  'Pending Approval': 'bg-amber-500/15 text-amber-400',
  'Partially Approved': 'bg-blue-500/15 text-blue-400',
  'Fully Approved': 'bg-green-500/15 text-green-400',
  'SO Rejected': 'bg-red-500/15 text-red-400',
  'In Procurement': 'bg-purple-500/15 text-purple-400',
  'Partially Procured': 'bg-fuchsia-500/15 text-fuchsia-400',
  'Ready for Billing': 'bg-cyan-500/15 text-cyan-400',
  'Billed': 'bg-teal-500/15 text-teal-400',
  'Partially Dispatched': 'bg-pink-500/15 text-pink-400',
  'Partially Delivered': 'bg-orange-500/15 text-orange-400',
  'Out for Delivery': 'bg-indigo-500/15 text-indigo-400',
  'Delivered': 'bg-emerald-500/15 text-emerald-400',
};

const blankItem = () => ({
  item_name: '', description: '', drawing_url: '', hsn_code: '',
  quantity_ordered: '', price_mode: 'net',
  list_price: '', discount_percent: '', net_price: ''
});

const STAGES = ['Approval', 'Procurement', 'Billing', 'Packing', 'On the Way', 'Delivered'];

function computeStage(order) {
  // Returns the highest active stage index (0..5) based on items
  if (!order) return 0;
  const items = order.items || [];
  if (order.overall_status === 'Delivered') return 5;
  if (order.overall_status === 'Out for Delivery') return 4;
  if (items.length) {
    const statuses = items.map(i => i.fulfillment_status);
    if (statuses.every(s => s === 'Delivered')) return 5;
    if (statuses.some(s => s === 'Dispatched' || s === 'Delivered')) return 4;
    if (statuses.some(s => s === 'Awaiting Dispatch')) return 3;
    if (statuses.some(s => s === 'Billed')) return 3;
    if (statuses.some(s => s === 'Ready to Pack')) return 2;
    // Has indents → procurement
    if (statuses.some(s => s === 'Awaiting Tally Check')) return 0;
  }
  if (['Pending Approval', 'Partially Approved'].includes(order.overall_status)) return 0;
  if (['Fully Approved', 'Approved'].includes(order.overall_status)) return 1;
  return 0;
}

function StageStepper({ order, isDark }) {
  const stage = computeStage(order);
  return (
    <div className="flex items-center gap-1.5 mt-1">
      {STAGES.map((s, i) => (
        <div key={s} className="flex items-center">
          <div
            title={s}
            className={`w-1.5 h-1.5 rounded-full ${
              i < stage
                ? 'bg-green-500'
                : i === stage
                ? 'bg-blue-500 ring-2 ring-blue-500/30'
                : isDark ? 'bg-white/15' : 'bg-gray-300'
            }`}
          />
          {i < STAGES.length - 1 && (
            <div className={`w-3 h-px ${i < stage ? 'bg-green-500/50' : isDark ? 'bg-white/10' : 'bg-gray-200'}`} />
          )}
        </div>
      ))}
      <span className={`ml-1 text-[10px] font-medium ${
        stage === 5 ? 'text-emerald-500' : isDark ? 'text-white/50' : 'text-gray-500'
      }`}>
        {STAGES[stage]}
      </span>
    </div>
  );
}

const FlowSalesOrders = () => {
  const { isDark, user } = useOutletContext();
  const [orders, setOrders] = useState([]);
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const [filterCompany, setFilterCompany] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 25;

  // Customer autocomplete
  const [custSuggestions, setCustSuggestions] = useState([]);
  const [showCustSuggest, setShowCustSuggest] = useState(false);

  const [form, setForm] = useState({
    company_id: '', customer_name: '', customer_email: '', customer_phone: '',
    customer_po_number: '', po_date: '',
    notes: '', po_proof_url: '',
    cgst_rate: 9, sgst_rate: 9, igst_rate: 0,
    items: [blankItem()]
  });

  // Item autocomplete state
  const [itemSuggest, setItemSuggest] = useState({}); // idx -> list
  const [itemSuggestOpen, setItemSuggestOpen] = useState({}); // idx -> bool
  const [manualItem, setManualItem] = useState({}); // idx -> bool (true = free-text manual entry)
  const [itemSearch, setItemSearch] = useState({}); // idx -> search text used in pick mode
  const itemSuggestTimers = useRef({}); // idx -> debounce timer for typing
  const debouncedFetchItemSuggest = (idx, q) => {
    if (itemSuggestTimers.current[idx]) clearTimeout(itemSuggestTimers.current[idx]);
    itemSuggestTimers.current[idx] = setTimeout(() => { fetchItemSuggest(idx, q); }, 250);
  };
  // Last-price hints per item index — { found, last_net_price, last_so_number, last_at, dismissed }
  const [lastPriceHint, setLastPriceHint] = useState({});
  // Only enable the last-price banner once the agent has EXPLICITLY picked a customer from
  // the autocomplete dropdown (or loaded an existing SO). Plain typing should NOT trigger it.
  const [customerPicked, setCustomerPicked] = useState(false);

  const cardCls = isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-gray-200';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-white border-gray-300 text-gray-900';
  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white dark-select' : 'bg-white border-gray-300 text-gray-900';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (filterCompany) params.company_id = filterCompany;
      if (filterStatus) params.status = filterStatus;
      if (search) params.q = search;
      const [oRes, fRes] = await Promise.all([getSalesOrders(params), getFirms()]);
      setOrders(oRes.data?.orders || []);
      setTotal(oRes.data?.total || 0);
      setFirms(fRes.data || []);
    } catch (err) { console.error('FlowSalesOrders: fetch failed', err); }
    finally { setLoading(false); }
  }, [filterCompany, filterStatus, search, page]);

  // Debounced search trigger
  useEffect(() => { fetchData(); }, [fetchData]);
  // Any filter/search change jumps back to page 1
  useEffect(() => { setPage(1); }, [filterCompany, filterStatus, search]);

  // Customer name autocomplete (per-agent, debounced)
  useEffect(() => {
    if (!dialogOpen) return;
    const t = setTimeout(async () => {
      try {
        const res = await getFlowMyCustomers(form.customer_name || '');
        setCustSuggestions(res.data || []);
      } catch { setCustSuggestions([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [form.customer_name, dialogOpen]);

  // Debounced last-price lookup — fires only AFTER the agent has explicitly picked a customer.
  // Plain typing in the customer field will NOT trigger this; the flag resets on every keystroke
  // in the customer input and gets re-armed by pickCustomer or by loading an existing SO for edit.
  const itemNamesKey = form.items.map(i => i.item_name).join('|');
  useEffect(() => {
    if (!dialogOpen) return;
    if (!customerPicked) {
      // Wipe any previously-shown hints when the agent un-picks (edits the customer field).
      setLastPriceHint(h => (Object.keys(h).length ? {} : h));
      return;
    }
    const customer = form.customer_name?.trim();
    if (!customer) return;
    const handles = form.items.map((it, idx) => {
      const name = it.item_name?.trim();
      if (!name) {
        setLastPriceHint(h => { if (!(idx in h)) return h; const n = { ...h }; delete n[idx]; return n; });
        return null;
      }
      return setTimeout(() => fetchLastPrice(idx, customer, name), 350);
    });
    return () => handles.forEach(h => h && clearTimeout(h));
  }, [form.customer_name, itemNamesKey, dialogOpen, customerPicked]);

  const handleUpload = async (file) => {
    if (!file) return null;
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('task_token');
    const res = await fetch(`${API_URL}/api/tasks-app/upload`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
    });
    const data = await res.json();
    return data.url || null;
  };

  const handlePoUpload = async (e) => {
    const url = await handleUpload(e.target.files?.[0]);
    if (url) { setForm(f => ({ ...f, po_proof_url: url })); toast.success('PO proof uploaded'); }
    else toast.error('Upload failed');
  };

  const handleDrawingUpload = async (idx, e) => {
    const url = await handleUpload(e.target.files?.[0]);
    if (url) {
      updateItem(idx, 'drawing_url', url);
      toast.success(`Drawing uploaded for item ${idx + 1}`);
    } else toast.error('Drawing upload failed');
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, blankItem()] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const setItemMode = (idx, manual) => {
    setManualItem(m => ({ ...m, [idx]: manual }));
    if (manual) {
      setItemSuggestOpen(s => ({ ...s, [idx]: false }));
    } else {
      // switching back to pick — reset the free-typed name so the agent must pick from the list
      setItemSearch(s => ({ ...s, [idx]: '' }));
      updateItem(idx, 'item_name', '');
    }
  };
  const updateItem = (idx, field, val) => setForm(f => {
    const items = [...f.items];
    items[idx] = { ...items[idx], [field]: val };
    // Recalculate net from list+discount if in 'list' mode
    if (items[idx].price_mode === 'list' && (field === 'list_price' || field === 'discount_percent')) {
      const lp = parseFloat(items[idx].list_price) || 0;
      const dp = parseFloat(items[idx].discount_percent) || 0;
      items[idx].net_price = (lp * (1 - dp / 100)).toFixed(2);
    }
    if (field === 'price_mode') {
      // when switching to net, clear discount/list
      if (val === 'net') { items[idx].list_price = ''; items[idx].discount_percent = ''; }
    }
    return { ...f, items };
  });

  // Totals
  const totals = useMemo(() => {
    const subtotal = form.items.reduce((s, it) => {
      const q = parseInt(it.quantity_ordered) || 0;
      const np = parseFloat(it.net_price) || 0;
      return s + q * np;
    }, 0);
    const cgst = subtotal * (parseFloat(form.cgst_rate) || 0) / 100;
    const sgst = subtotal * (parseFloat(form.sgst_rate) || 0) / 100;
    const igst = subtotal * (parseFloat(form.igst_rate) || 0) / 100;
    return { subtotal, cgst, sgst, igst, total: subtotal + cgst + sgst + igst };
  }, [form.items, form.cgst_rate, form.sgst_rate, form.igst_rate]);

  const pickCustomer = (c) => {
    setForm(f => ({
      ...f,
      customer_name: c.customer_name,
      customer_email: c.customer_email || f.customer_email,
      customer_phone: c.customer_phone || f.customer_phone,
    }));
    setShowCustSuggest(false);
    setCustomerPicked(true);  // arm last-price lookups — the debounced useEffect picks it up
  };

  const fetchItemSuggest = async (idx, q) => {
    try {
      // Run both in parallel: past-SO items + latest Tally stock snapshot for the chosen firm
      const stockReq = searchStockItems({ q: q || '', firm_id: form.company_id || undefined, limit: 30, only_in_stock: false });
      const histReq = getFlowMyItems(q);
      const [stockRes, histRes] = await Promise.allSettled([stockReq, histReq]);

      const stockRows = (stockRes.status === 'fulfilled' ? stockRes.value.data?.items || [] : []).map(s => ({
        item_name: s.particulars,
        source: 'stock',
        tally_qty: s.quantity,
        firm_code: s.firm_code,
        group: s.group,
      }));
      const histRows = (histRes.status === 'fulfilled' ? histRes.value.data || [] : []).map(h => ({
        ...h, source: 'history'
      }));

      // De-dupe by lowercase name; stock entries win the qty info, history wins price/HSN
      const map = new Map();
      for (const h of histRows) {
        map.set((h.item_name || '').toLowerCase(), h);
      }
      for (const s of stockRows) {
        const k = (s.item_name || '').toLowerCase();
        const existing = map.get(k) || {};
        map.set(k, { ...existing, ...s, item_name: s.item_name, tally_qty: s.tally_qty, firm_code: s.firm_code, group: s.group });
      }
      // Order: stock matches first (so user sees current qty), then pure history
      const merged = Array.from(map.values()).sort((a, b) => {
        const at = a.tally_qty !== undefined ? 0 : 1;
        const bt = b.tally_qty !== undefined ? 0 : 1;
        return at - bt;
      });
      setItemSuggest(s => ({ ...s, [idx]: merged }));
    } catch { setItemSuggest(s => ({ ...s, [idx]: [] })); }
  };

  const pickItem = (idx, it) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = {
        ...items[idx],
        item_name: it.item_name,
        description: it.description || items[idx].description,
        hsn_code: it.hsn_code || items[idx].hsn_code,
        net_price: it.net_price ? String(it.net_price) : items[idx].net_price,
        list_price: it.list_price ? String(it.list_price) : items[idx].list_price,
        discount_percent: it.discount_percent ? String(it.discount_percent) : items[idx].discount_percent,
      };
      return { ...f, items };
    });
    setItemSuggestOpen(s => ({ ...s, [idx]: false }));
    setItemSearch(s => ({ ...s, [idx]: '' }));
    // Only check last-price IF the agent has already picked the customer from the dropdown.
    // The debounced useEffect (gated on customerPicked) will pick it up too — this is a redundant
    // call to give immediate feedback when the item is picked AFTER the customer is locked-in.
    if (customerPicked && form.customer_name?.trim()) {
      fetchLastPrice(idx, form.customer_name, it.item_name);
    }
  };

  // Fetch last-used price for (customer + item) so the agent can re-use it instead of guessing.
  // Guarded by customerPicked so it NEVER fires until the agent explicitly selects a customer.
  const fetchLastPrice = useCallback(async (idx, customer, itemName) => {
    if (!customerPicked || !customer?.trim() || !itemName?.trim()) {
      setLastPriceHint(h => { const n = { ...h }; delete n[idx]; return n; });
      return;
    }
    try {
      const res = await getCustomerItemLastPrice(customer.trim(), itemName.trim());
      if (res.data?.found) {
        setLastPriceHint(h => ({ ...h, [idx]: { ...res.data, dismissed: false } }));
      } else {
        setLastPriceHint(h => { const n = { ...h }; delete n[idx]; return n; });
      }
    } catch { /* silent */ }
  }, [customerPicked]);

  const applyLastPrice = (idx) => {
    const hint = lastPriceHint[idx];
    if (!hint) return;
    setForm(f => {
      const items = [...f.items];
      const mode = hint.last_price_mode === 'list' ? 'list' : 'net';
      items[idx] = {
        ...items[idx],
        price_mode: mode,
        net_price: hint.last_net_price ? String(hint.last_net_price) : items[idx].net_price,
        list_price: hint.last_list_price ? String(hint.last_list_price) : '',
        discount_percent: hint.last_discount_percent != null ? String(hint.last_discount_percent) : '',
      };
      return { ...f, items };
    });
    setLastPriceHint(h => ({ ...h, [idx]: { ...h[idx], dismissed: true } }));
    toast.success(`Applied last price ₹${hint.last_net_price} from ${hint.last_so_number}`);
  };

  const dismissLastPrice = (idx) => {
    setLastPriceHint(h => ({ ...h, [idx]: { ...h[idx], dismissed: true } }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_id) { toast.error('Select a company'); return; }
    if (!form.customer_name.trim()) { toast.error('Customer name is required'); return; }
    const email = (form.customer_email || '').trim();
    if (!email) { toast.error('Customer email is required'); return; }
    // RFC 5322 lite — Indian-common-case safe
    if (!/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(email)) {
      toast.error('Enter a valid email address (e.g. name@company.com)'); return;
    }
    const phone = (form.customer_phone || '').replace(/[\s-]/g, '');
    if (!phone) { toast.error('Customer phone is required'); return; }
    // Accept: 10-digit Indian mobile (6-9 start), optional +91 / 91 / 0 prefix
    if (!/^(?:\+?91|0)?[6-9]\d{9}$/.test(phone)) {
      toast.error('Enter a valid 10-digit Indian mobile (starts with 6-9)'); return;
    }
    if (!form.po_date) { toast.error('PO Date is required'); return; }
    if (!form.po_proof_url) { toast.error('PO proof / conversation proof is required'); return; }
    if (!form.items.length || !form.items[0].item_name.trim()) { toast.error('Add at least one item'); return; }
    for (let i = 0; i < form.items.length; i++) {
      const it = form.items[i];
      if (!it.item_name.trim()) { toast.error(`Item ${i + 1}: Name required`); return; }
      const q = parseInt(it.quantity_ordered);
      if (!q || q <= 0) { toast.error(`Item ${i + 1}: Quantity must be > 0`); return; }
      const np = parseFloat(it.net_price);
      if (!np || np <= 0) { toast.error(`Item ${i + 1}: Net price must be > 0`); return; }
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: form.items.map(it => ({
          ...it,
          quantity_ordered: parseInt(it.quantity_ordered),
          list_price: parseFloat(it.list_price) || 0,
          discount_percent: parseFloat(it.discount_percent) || 0,
          net_price: parseFloat(it.net_price)
        }))
      };
      if (editingOrder) {
        await updateSalesOrder(editingOrder.id, payload);
        toast.success('Sales Order updated!');
      } else {
        // Duplicacy guard: same items still pending on this customer's earlier SOs
        try {
          const chk = await checkPendingItems({ customer_name: form.customer_name, item_names: form.items.map(i => i.item_name) });
          const pend = chk.data?.pending || [];
          if (pend.length) {
            const msg = '⚠ Same item(s) still PENDING for this customer:\n\n'
              + pend.map(p => `• ${p.item_name} — ${p.pending_qty} qty pending on ${p.so_number} (${p.status})`).join('\n')
              + '\n\nTo avoid over-stocking / duplicate ordering, confirm you still want to create this new SO?';
            if (!window.confirm(msg)) { setSubmitting(false); return; }
          }
        } catch { /* non-blocking */ }
        await createSalesOrder(payload);
        toast.success('Sales Order created!');
      }
      setDialogOpen(false);
      setEditingOrder(null);
      setForm({
        company_id: '', customer_name: '', customer_email: '', customer_phone: '',
        customer_po_number: '', po_date: '',
        notes: '', po_proof_url: '', cgst_rate: 9, sgst_rate: 9, igst_rate: 0, items: [blankItem()]
      });
      setManualItem({}); setItemSearch({});
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSubmitting(false); }
  };

  const handleViewOrder = async (id) => {
    try {
      const res = await getSalesOrder(id);
      setViewOrder(res.data);
    } catch { toast.error('Failed to load order'); }
  };

  const handleDownloadPdf = async (o) => {
    try {
      const res = await downloadSalesOrderPdf(o.id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${o.so_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('PDF download failed'); }
  };

  const handleDeleteSO = async (o) => {
    if (!window.confirm(`Permanently delete Sales Order ${o.so_number}?\n\nThis will also remove all related items, indents, store verifications and dispatch lots. This action cannot be undone from the UI.`)) return;
    try {
      const res = await deleteSalesOrder(o.id);
      toast.success(res.data?.message || 'Sales Order deleted');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const handleEdit = async (o) => {
    try {
      const res = await getSalesOrder(o.id);
      const full = res.data;
      // Check edit-allowed (any decision locks for non-admin)
      const anyDecided = (full.items || []).some(it => ['Approved', 'Rejected', 'Hold'].includes(it.approval_status));
      if (anyDecided) {
        // Backend will enforce 403 for non-admin; still attempt
        if (!window.confirm('This order has decisions logged. Editing requires admin access. Proceed?')) return;
      }
      setEditingOrder(full);
      setForm({
        company_id: full.company_id || '',
        customer_name: full.customer_name || '',
        customer_email: full.customer_email || '',
        customer_phone: full.customer_phone || '',
        customer_po_number: full.customer_po_number || '',
        po_date: full.po_date || '',
        notes: full.notes || '',
        po_proof_url: full.po_proof_url || '',
        cgst_rate: full.cgst_rate || 0,
        sgst_rate: full.sgst_rate || 0,
        igst_rate: full.igst_rate || 0,
        items: (full.items || []).map(it => ({
          id: it.id, item_name: it.item_name, description: it.description || '',
          drawing_url: it.drawing_url || '', hsn_code: it.hsn_code || '',
          quantity_ordered: it.quantity_ordered,
          price_mode: it.price_mode || 'net',
          list_price: it.list_price || '',
          discount_percent: it.discount_percent || '',
          net_price: it.net_price || it.unit_price || ''
        }))
      });
      // Existing SO already has a confirmed customer — arm the flag so price suggestions still surface on edit.
      setCustomerPicked(!!(full.customer_name || '').trim());
      // Existing line items keep their (already-chosen) names editable in manual mode.
      const modes = {}; (full.items || []).forEach((_, i) => { modes[i] = true; });
      setManualItem(modes);
      setItemSearch({});
      setLastPriceHint({});
      setDialogOpen(true);
    } catch { toast.error('Failed to load order'); }
  };

  const getFirmName = (id) => firms.find(f => f.id === id)?.name || id;

  return (
    <div className="space-y-4" data-testid="flow-sales-orders">
      <div className="flex flex-col gap-3">
        <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Sales Orders</h1>
        <div className="grid grid-cols-1 sm:flex sm:items-center gap-2 sm:flex-wrap">
          <Input
            placeholder="Search SO #, customer, PO #..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`h-9 w-full sm:w-56 text-xs ${isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : ''}`}
            data-testid="so-search-input"
          />
          <div className="grid grid-cols-2 sm:flex gap-2">
            <select className={`h-9 rounded-lg text-xs px-3 border ${selectCls}`} value={filterCompany} onChange={e => setFilterCompany(e.target.value)}>
              <option value="">All Companies</option>
              {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <select className={`h-9 rounded-lg text-xs px-3 border ${selectCls}`} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
              <option value="">All Status</option>
              {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <Button onClick={() => { setEditingOrder(null); setCustomerPicked(false); setLastPriceHint({}); setManualItem({}); setItemSearch({}); setForm({ company_id: '', customer_name: '', customer_email: '', customer_phone: '', customer_po_number: '', po_date: '', notes: '', po_proof_url: '', cgst_rate: 9, sgst_rate: 9, igst_rate: 0, items: [blankItem()] }); setDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700 h-9 w-full sm:w-auto" data-testid="create-so-btn">
            <Plus size={16} className="mr-1" /> New Sales Order
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className={`h-20 rounded-xl animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} />)}</div>
      ) : orders.length === 0 ? (
        <Card className={cardCls}><CardContent className="p-8 text-center">
          <Package size={40} className={`mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
          <p className={isDark ? 'text-white/40' : 'text-gray-500'}>No sales orders yet</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {orders.map(o => (
            <Card key={o.id} className={`${cardCls} transition-all`}>
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-start justify-between gap-2 cursor-pointer" onClick={() => setExpandedId(expandedId === o.id ? null : o.id)}>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 flex-1 min-w-0">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{o.so_number}</p>
                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[o.overall_status] || 'bg-gray-500/15 text-gray-400'}`}>{o.overall_status}</span>
                      </div>
                      <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{getFirmName(o.company_id)} · {o.created_at?.split('T')[0]}</p>
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm truncate ${isDark ? 'text-white/70' : 'text-gray-700'}`}>{o.customer_name}</p>
                      <p className={`text-xs ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{o.agent_name} · <span className="font-semibold">₹{(o.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></p>
                    </div>
                  </div>
                  <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0 flex-wrap justify-end">
                    <Button size="sm" variant="ghost" className={`h-8 w-8 p-0 ${isDark ? 'text-white/40' : 'text-gray-400'}`} onClick={(e) => { e.stopPropagation(); handleDownloadPdf(o); }} title="Download PDF" data-testid={`download-pdf-${o.id}`}>
                      <Download size={16} />
                    </Button>
                    <Button size="sm" variant="ghost" className={`h-8 w-8 p-0 ${isDark ? 'text-white/40' : 'text-gray-400'}`} onClick={(e) => { e.stopPropagation(); handleEdit(o); }} title="Edit" data-testid={`edit-so-${o.id}`}>
                      <Pencil size={14} />
                    </Button>
                    <Button size="sm" variant="ghost" className={`h-8 w-8 p-0 ${isDark ? 'text-white/40' : 'text-gray-400'}`} onClick={(e) => { e.stopPropagation(); handleViewOrder(o.id); }}>
                      <Eye size={16} />
                    </Button>
                    {user?.role === 'admin' && (
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:bg-red-500/10" onClick={(e) => { e.stopPropagation(); handleDeleteSO(o); }} title="Delete (admin only)" data-testid={`delete-so-${o.id}`}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                    {expandedId === o.id ? <ChevronUp size={16} className={isDark ? 'text-white/30' : 'text-gray-400'} /> : <ChevronDown size={16} className={isDark ? 'text-white/30' : 'text-gray-400'} />}
                  </div>
                </div>
                <StageStepper order={o} isDark={isDark} />
              </CardContent>
            </Card>
          ))}
          {total > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-3 pt-2" data-testid="so-pagination">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}
                className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''} data-testid="so-prev-page">Prev</Button>
              <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                Page {page} of {Math.ceil(total / PAGE_SIZE)} · {total} orders
              </span>
              <Button size="sm" variant="outline" disabled={page >= Math.ceil(total / PAGE_SIZE)} onClick={() => setPage(p => p + 1)}
                className={isDark ? 'border-white/20 text-white hover:bg-white/10' : ''} data-testid="so-next-page">Next</Button>
            </div>
          )}
        </div>
      )}

      {/* View Order Dialog */}
      <Dialog open={!!viewOrder} onOpenChange={() => setViewOrder(null)}>
        <DialogContent className={`max-w-4xl w-[95vw] max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`}>
          {viewOrder && (
            <>
              <DialogHeader>
                <DialogTitle className={isDark ? 'text-white' : ''}>{viewOrder.so_number} — {viewOrder.customer_name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div><span className={isDark ? 'text-white/40' : 'text-gray-500'}>Company:</span> {getFirmName(viewOrder.company_id)}</div>
                  <div><span className={isDark ? 'text-white/40' : 'text-gray-500'}>Agent:</span> {viewOrder.agent_name}</div>
                  <div><span className={isDark ? 'text-white/40' : 'text-gray-500'}>Status:</span> <span className="font-semibold">{viewOrder.overall_status}</span></div>
                  <div><span className={isDark ? 'text-white/40' : 'text-gray-500'}>Email:</span> {viewOrder.customer_email || '-'}</div>
                  <div><span className={isDark ? 'text-white/40' : 'text-gray-500'}>Phone:</span> {viewOrder.customer_phone || '-'}</div>
                  <div><span className={isDark ? 'text-white/40' : 'text-gray-500'}>Total:</span> ₹{(viewOrder.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
                {viewOrder.po_proof_url && (
                  <a href={viewOrder.po_proof_url.startsWith("http") ? viewOrder.po_proof_url : `${API_URL}${viewOrder.po_proof_url.startsWith("/api/") ? "" : "/api/files/"}${viewOrder.po_proof_url}`} target="_blank" rel="noreferrer" className="text-blue-400 text-xs underline">View PO Proof</a>
                )}

                {/* TAT */}
                {viewOrder.tat && (
                  <div className={`p-3 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>Turnaround Time (hours)</p>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-[11px]">
                      {[
                        ['Sales→Approval', viewOrder.tat.sales_to_approval_hrs],
                        ['Store/Tally', viewOrder.tat.store_tally_hrs],
                        ['Billing', viewOrder.tat.billing_hrs],
                        ['Packing', viewOrder.tat.store_packing_hrs],
                        ['Dispatch', viewOrder.tat.logistics_dispatch_hrs],
                        ['In Transit', viewOrder.tat.delivery_transit_hrs],
                      ].map(([label, val]) => (
                        <div key={label} className="text-center">
                          <p className={isDark ? 'text-white/40' : 'text-gray-400'}>{label}</p>
                          <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{val != null ? `${val}h` : '—'}</p>
                        </div>
                      ))}
                    </div>
                    {viewOrder.tat.total_hrs != null && (
                      <p className={`text-[11px] mt-2 text-center ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                        Total: <b>{viewOrder.tat.total_hrs}h</b> {viewOrder.tat.completed ? '(completed)' : '(in progress)'}
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <p className={`text-sm font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Line Items ({viewOrder.items?.length || 0})</p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className={`border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                          <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>#</th>
                          <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Item / Description</th>
                          <th className={`text-center py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Qty Progress</th>
                          <th className={`text-right py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Net Price</th>
                          <th className={`text-right py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Amount</th>
                          <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Approval</th>
                          <th className={`text-left py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Fulfillment</th>
                          <th className={`text-center py-2 px-2 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Drawing</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(viewOrder.items || []).map((item, i) => {
                          const ordered = item.quantity_ordered || 0;
                          const approved = item.quantity_approved ?? ordered;
                          const allocated = item.quantity_allocated_to_bill || 0;
                          const billed = item.quantity_billed || 0;
                          const dispatched = item.quantity_dispatched || 0;
                          const delivered = item.quantity_delivered || 0;
                          const pendingAllocation = item.quantity_pending_allocation || 0;
                          const pendingDispatch = Math.max(approved - dispatched, 0);
                          return (
                          <tr key={item.id} className={`border-b ${isDark ? 'border-white/5' : 'border-gray-50'}`}>
                            <td className={`py-2 px-2 ${isDark ? 'text-white/30' : 'text-gray-400'}`}>{i + 1}</td>
                            <td className={`py-2 px-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              <div className="font-medium">{item.item_name}</div>
                              {item.description && <div className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{item.description}</div>}
                            </td>
                            <td className="py-2 px-2 text-center">
                              <div className={`text-[11px] font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Order: {ordered}</div>
                              <div className="text-[10px] mt-0.5 space-y-0.5 leading-tight">
                                {approved !== ordered && <div className={isDark ? 'text-white/50' : 'text-gray-500'}>Approved: <b>{approved}</b></div>}
                                {dispatched > 0 && <div className="text-blue-400">Dispatched: <b>{dispatched}</b></div>}
                                {delivered > 0 && <div className="text-green-400">Delivered: <b>{delivered}</b></div>}
                                {pendingDispatch > 0 && (dispatched > 0 || delivered > 0) && <div className="text-pink-400">Pending: <b>{pendingDispatch}</b></div>}
                                {pendingAllocation > 0 && <div className="text-amber-400">Pending Alloc: <b>{pendingAllocation}</b></div>}
                                {allocated > 0 && billed === 0 && <div className={isDark ? 'text-white/50' : 'text-gray-500'}>Allocated: <b>{allocated}</b></div>}
                                {billed > 0 && billed !== dispatched && <div className={isDark ? 'text-white/50' : 'text-gray-500'}>Billed: <b>{billed}</b></div>}
                              </div>
                              {approved > 0 && (
                                <div className={`mt-1 h-1 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-200'}`}>
                                  <div className="h-full bg-gradient-to-r from-blue-500 to-green-500" style={{ width: `${Math.min(100, (delivered / approved) * 100)}%` }} />
                                </div>
                              )}
                            </td>
                            <td className="py-2 px-2 text-right">₹{(item.net_price || 0).toLocaleString('en-IN')}</td>
                            <td className="py-2 px-2 text-right">₹{(item.amount || 0).toLocaleString('en-IN')}</td>
                            <td className={`py-2 px-2 ${item.approval_status === 'Approved' ? 'text-green-400' : item.approval_status === 'Rejected' ? 'text-red-400' : isDark ? 'text-white/50' : 'text-gray-500'}`}>{item.approval_status}</td>
                            <td className={`py-2 px-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                              <div className="flex items-center gap-1">
                                <span>{item.fulfillment_status}</span>
                                {user?.role === 'admin' && ['Approved','Awaiting Tally Check','Awaiting Procurement','Ready to Pack','Billed','Awaiting Dispatch','Pushed to Logistics','Dispatched','Partially Dispatched'].includes(item.fulfillment_status) && (
                                  <button
                                    type="button"
                                    onClick={async () => {
                                      const reason = window.prompt(`↩ Revert "${item.item_name}" from "${item.fulfillment_status}" one step back?\n\nEnter reason (min 5 chars):`, '');
                                      if (reason === null) return;
                                      if (!reason.trim() || reason.trim().length < 5) { toast.error('Reason must be at least 5 characters'); return; }
                                      try {
                                        const res = await adminRevertItem(item.id, reason.trim());
                                        toast.success(res.data.message);
                                        // Refresh
                                        if (typeof fetchData === 'function') fetchData();
                                      } catch (err) { toast.error(err.response?.data?.detail || 'Revert failed'); }
                                    }}
                                    title="Revert one step (admin only)"
                                    className={`text-[10px] px-1.5 py-0.5 rounded border ${isDark ? 'border-orange-400/40 text-orange-300 hover:bg-orange-500/10' : 'border-orange-500/40 text-orange-600 hover:bg-orange-50'}`}
                                    data-testid={`so-revert-${item.id}`}
                                  >↩</button>
                                )}
                              </div>
                            </td>
                            <td className="py-2 px-2 text-center">
                              {item.drawing_url ? (
                                <a href={item.drawing_url.startsWith("http") ? item.drawing_url : `${API_URL}${item.drawing_url.startsWith("/api/") ? "" : "/api/files/"}${item.drawing_url}`} target="_blank" rel="noreferrer" className="text-blue-400"><ImgIcon size={14} className="inline" /></a>
                              ) : '-'}
                            </td>
                          </tr>
                        );})}
                      </tbody>
                      <tfoot>
                        <tr className={`border-t ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                          <td colSpan={4} className={`py-2 px-2 text-right font-semibold ${isDark ? 'text-white/70' : 'text-gray-700'}`}>Subtotal</td>
                          <td className={`py-2 px-2 text-right font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>₹{(viewOrder.subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td colSpan={3}></td>
                        </tr>
                        {viewOrder.cgst_amount > 0 && (
                          <tr><td colSpan={4} className={`py-1 px-2 text-right text-[11px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>CGST @ {viewOrder.cgst_rate}%</td>
                            <td className="py-1 px-2 text-right">₹{viewOrder.cgst_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td><td colSpan={3}></td></tr>
                        )}
                        {viewOrder.sgst_amount > 0 && (
                          <tr><td colSpan={4} className={`py-1 px-2 text-right text-[11px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>SGST @ {viewOrder.sgst_rate}%</td>
                            <td className="py-1 px-2 text-right">₹{viewOrder.sgst_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td><td colSpan={3}></td></tr>
                        )}
                        {viewOrder.igst_amount > 0 && (
                          <tr><td colSpan={4} className={`py-1 px-2 text-right text-[11px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>IGST @ {viewOrder.igst_rate}%</td>
                            <td className="py-1 px-2 text-right">₹{viewOrder.igst_amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td><td colSpan={3}></td></tr>
                        )}
                        <tr>
                          <td colSpan={4} className={`py-2 px-2 text-right font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Total</td>
                          <td className={`py-2 px-2 text-right font-bold text-base ${isDark ? 'text-white' : 'text-gray-900'}`}>₹{(viewOrder.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                          <td colSpan={3}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Sales Order Dialog */}
      <Dialog open={dialogOpen} onOpenChange={v => {
        if (!v) {
          if (!window.confirm('Close the Sales Order form?\n\nAny unsaved data will be LOST.')) return;
          setDialogOpen(false); setEditingOrder(null);
        }
      }}>
        <DialogContent className={`max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto ${isDark ? 'bg-[#0f1729] border-white/10 text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle className={isDark ? 'text-white' : ''}>{editingOrder ? `Edit Sales Order — ${editingOrder.so_number}` : 'New Sales Order'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className={isDark ? 'text-white/60' : ''}>Company *</Label>
                <select className={`w-full h-9 rounded-lg text-sm px-3 border mt-1 ${selectCls}`} value={form.company_id} onChange={e => setForm(f => ({ ...f, company_id: e.target.value }))} required data-testid="so-company">
                  <option value="">Select Company</option>
                  {firms.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <div className="relative">
                <Label className={isDark ? 'text-white/60' : ''}>Customer Name *</Label>
                <Input
                  className={`mt-1 ${inputCls}`}
                  value={form.customer_name}
                  onChange={e => { setForm(f => ({ ...f, customer_name: e.target.value })); setShowCustSuggest(true); setCustomerPicked(false); }}
                  onFocus={() => setShowCustSuggest(true)}
                  onBlur={() => setTimeout(() => setShowCustSuggest(false), 200)}
                  placeholder="Type to search your customers..."
                  required
                  data-testid="so-customer"
                  autoComplete="off"
                />
                {showCustSuggest && custSuggestions.length > 0 && (
                  <div className={`absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto rounded-lg shadow-lg border ${isDark ? 'bg-[#0c1222] border-white/10' : 'bg-white border-gray-200'}`}>
                    {custSuggestions.map((c, i) => (
                      <button
                        key={`${c.customer_name}-${i}`}
                        type="button"
                        onMouseDown={() => pickCustomer(c)}
                        className={`w-full text-left px-3 py-2 text-xs hover:bg-blue-500/10 ${isDark ? 'text-white border-b border-white/5' : 'text-gray-900 border-b border-gray-50'}`}
                        data-testid={`customer-suggest-${i}`}
                      >
                        <div className="font-medium">{c.customer_name}</div>
                        <div className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{c.customer_email || c.customer_phone || ''}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <Label className={isDark ? 'text-white/60' : ''}>Customer Email *</Label>
                <Input className={`mt-1 ${inputCls}`} type="email" value={form.customer_email} onChange={e => setForm(f => ({ ...f, customer_email: e.target.value }))} placeholder="name@company.com" required data-testid="so-customer-email" />
              </div>
              <div>
                <Label className={isDark ? 'text-white/60' : ''}>Customer Phone *</Label>
                <Input className={`mt-1 ${inputCls}`} type="tel" inputMode="tel" pattern="(?:\+?91|0)?[6-9][0-9]{9}" value={form.customer_phone} onChange={e => setForm(f => ({ ...f, customer_phone: e.target.value }))} placeholder="10-digit mobile (6-9...)" required data-testid="so-customer-phone" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className={isDark ? 'text-white/60' : ''}>Customer PO Number</Label>
                <Input className={`mt-1 ${inputCls}`} value={form.customer_po_number} onChange={e => setForm(f => ({ ...f, customer_po_number: e.target.value }))} placeholder="Optional" data-testid="so-po-number" />
              </div>
              <div>
                <Label className={isDark ? 'text-white/60' : ''}>PO Date *</Label>
                <Input type="date" className={`mt-1 ${inputCls}`} value={form.po_date} onChange={e => setForm(f => ({ ...f, po_date: e.target.value }))} required data-testid="so-po-date" />
              </div>
            </div>

            <div>
              <Label className={isDark ? 'text-white/60' : ''}>PO Proof *</Label>
              <div className="mt-1 flex items-center gap-3">
                <label className={`flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer text-sm ${isDark ? 'border-white/20 text-white/60 hover:bg-white/5' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                  <Upload size={16} /> {form.po_proof_url ? 'Uploaded' : 'Upload File'}
                  <input type="file" accept="image/*,.pdf" className="hidden" onChange={handlePoUpload} />
                </label>
                {form.po_proof_url && <span className="text-green-400 text-xs">✓ uploaded</span>}
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className={isDark ? 'text-white/60' : ''}>Line Items *</Label>
                <Button type="button" size="sm" variant="ghost" onClick={addItem} className={isDark ? 'text-blue-400' : 'text-blue-600'}>
                  <Plus size={14} className="mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-3">
                {form.items.map((item, i) => (
                  <div key={i} className={`p-3 rounded-lg border ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs font-semibold ${isDark ? 'text-white/60' : 'text-gray-600'}`}>Item {i + 1}</span>
                      {form.items.length > 1 && (
                        <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => removeItem(i)}>
                          <Trash2 size={12} />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-12 gap-2 mb-2">
                      <div className="col-span-6 relative">
                        <div className="flex items-center gap-1 mb-1">
                          <button type="button" onClick={() => setItemMode(i, false)}
                            className={`px-2 py-0.5 rounded text-[10px] font-medium border transition-colors ${!manualItem[i] ? 'bg-blue-600 text-white border-blue-600' : isDark ? 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                            data-testid={`item-mode-pick-${i}`}>Pick from list</button>
                          <button type="button" onClick={() => setItemMode(i, true)}
                            className={`px-2 py-0.5 rounded text-[10px] font-medium border transition-colors ${manualItem[i] ? 'bg-blue-600 text-white border-blue-600' : isDark ? 'bg-white/5 text-white/50 border-white/10 hover:bg-white/10' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                            data-testid={`item-mode-manual-${i}`}>Add manually</button>
                        </div>
                        {manualItem[i] ? (
                          <Input
                            className={`h-8 text-xs ${inputCls}`}
                            placeholder="Type custom item name *"
                            value={item.item_name}
                            onChange={e => updateItem(i, 'item_name', e.target.value)}
                            required
                            autoComplete="off"
                            data-testid={`item-name-${i}`}
                          />
                        ) : (
                          <>
                            <Input
                              className={`h-8 text-xs ${inputCls}`}
                              placeholder="Search & select a listed item *"
                              value={item.item_name || itemSearch[i] || ''}
                              onChange={e => { setItemSearch(s => ({ ...s, [i]: e.target.value })); updateItem(i, 'item_name', ''); debouncedFetchItemSuggest(i, e.target.value); setItemSuggestOpen(s => ({ ...s, [i]: true })); }}
                              onFocus={() => { fetchItemSuggest(i, itemSearch[i] || ''); setItemSuggestOpen(s => ({ ...s, [i]: true })); }}
                              onBlur={() => setTimeout(() => setItemSuggestOpen(s => ({ ...s, [i]: false })), 200)}
                              autoComplete="off"
                              data-testid={`item-name-${i}`}
                            />
                            {item.item_name && (
                              <span className={`absolute right-2 top-[30px] text-[10px] font-semibold px-1.5 py-0.5 rounded ${isDark ? 'bg-green-500/15 text-green-400' : 'bg-green-50 text-green-600'}`}>listed ✓</span>
                            )}
                            {itemSuggestOpen[i] && (itemSuggest[i] || []).length > 0 && (
                              <div className={`absolute z-30 left-0 right-0 mt-1 max-h-44 overflow-y-auto rounded-lg shadow-lg border text-xs ${isDark ? 'bg-[#0c1222] border-white/10' : 'bg-white border-gray-200'}`}>
                                {(itemSuggest[i] || []).slice(0, 12).map((s, k) => (
                                  <button key={k} type="button" onMouseDown={() => pickItem(i, s)}
                                    className={`w-full text-left px-3 py-1.5 hover:bg-blue-500/10 ${isDark ? 'text-white border-b border-white/5' : 'text-gray-900 border-b border-gray-50'}`}
                                    data-testid={`item-suggest-${i}-${k}`}>
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="font-medium truncate">{s.item_name}</span>
                                      {typeof s.tally_qty === 'number' && (
                                        <span className={`text-[10px] font-semibold whitespace-nowrap px-1.5 py-0.5 rounded ${s.tally_qty > 0 ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'}`}>
                                          Tally: {Math.round(s.tally_qty)}{s.firm_code ? ` (${s.firm_code})` : ''}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      {s.hsn_code && <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>HSN: {s.hsn_code}</span>}
                                      {s.net_price > 0 && <span className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>₹{s.net_price}</span>}
                                      {s.group && <span className={`text-[10px] ${isDark ? 'text-white/30' : 'text-gray-400'}`}>· {s.group}</span>}
                                    </div>
                                  </button>
                                ))}
                              </div>
                            )}
                            {itemSuggestOpen[i] && (itemSearch[i] || '').trim().length > 0 && (itemSuggest[i] || []).length === 0 && (
                              <div className={`absolute z-30 left-0 right-0 mt-1 rounded-lg shadow-lg border text-[10px] px-3 py-2 ${isDark ? 'bg-[#0c1222] border-white/10 text-white/50' : 'bg-white border-gray-200 text-gray-500'}`}>
                                No listed item matches. Click <b>Add manually</b> to enter a custom item.
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      <Input className={`col-span-3 h-8 text-xs ${inputCls}`} placeholder="HSN" value={item.hsn_code} onChange={e => updateItem(i, 'hsn_code', e.target.value)} />
                      <Input className={`col-span-3 h-8 text-xs ${inputCls}`} type="number" min="1" placeholder="Qty *" value={item.quantity_ordered} onChange={e => updateItem(i, 'quantity_ordered', e.target.value)} required />
                    </div>
                    <Input className={`h-8 text-xs mb-2 ${inputCls}`} placeholder="Description (specifications, dimensions, etc.)" value={item.description} onChange={e => updateItem(i, 'description', e.target.value)} />
                    {lastPriceHint[i] && lastPriceHint[i].found && !lastPriceHint[i].dismissed && (
                      <div className={`flex items-center justify-between gap-3 mb-2 px-3 py-2 rounded-lg border text-xs ${isDark ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`} data-testid={`last-price-hint-${i}`}>
                        <div className="min-w-0">
                          <div className="font-semibold">Last sold to this customer at ₹{lastPriceHint[i].last_net_price}</div>
                          <div className={`text-[10px] ${isDark ? 'text-emerald-300/80' : 'text-emerald-700/80'}`}>
                            {lastPriceHint[i].last_so_number} · {lastPriceHint[i].last_qty || 0} units · {(lastPriceHint[i].last_at || '').split('T')[0]}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button type="button" size="sm" className="h-7 px-3 bg-emerald-600 hover:bg-emerald-700 text-[10px]" onClick={() => applyLastPrice(i)} data-testid={`apply-last-price-${i}`}>Use this price</Button>
                          <Button type="button" size="sm" variant="ghost" className={`h-7 px-2 text-[10px] ${isDark ? 'text-emerald-200/70 hover:bg-white/5' : 'text-emerald-700/70 hover:bg-white'}`} onClick={() => dismissLastPrice(i)} data-testid={`dismiss-last-price-${i}`}>Dismiss</Button>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-3">
                        <Label className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Price Mode</Label>
                        <select className={`mt-1 w-full h-8 rounded text-xs px-2 border ${selectCls}`} value={item.price_mode} onChange={e => updateItem(i, 'price_mode', e.target.value)}>
                          <option value="net">Net Price</option>
                          <option value="list">List Price</option>
                        </select>
                      </div>
                      {item.price_mode === 'list' ? (
                        <>
                          <div className="col-span-3">
                            <Label className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>List Price *</Label>
                            <Input className={`mt-1 h-8 text-xs ${inputCls}`} type="number" min="0" step="0.01" value={item.list_price} onChange={e => updateItem(i, 'list_price', e.target.value)} required />
                          </div>
                          <div className="col-span-2">
                            <Label className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Disc %</Label>
                            <Input className={`mt-1 h-8 text-xs ${inputCls}`} type="number" min="0" max="100" step="0.01" value={item.discount_percent} onChange={e => updateItem(i, 'discount_percent', e.target.value)} />
                          </div>
                          <div className="col-span-2">
                            <Label className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Net (auto)</Label>
                            <Input className={`mt-1 h-8 text-xs ${inputCls} font-semibold`} type="number" value={item.net_price} readOnly />
                          </div>
                        </>
                      ) : (
                        <div className="col-span-5">
                          <Label className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Net Price *</Label>
                          <Input className={`mt-1 h-8 text-xs ${inputCls}`} type="number" min="0" step="0.01" value={item.net_price} onChange={e => updateItem(i, 'net_price', e.target.value)} required data-testid={`item-net-${i}`} />
                        </div>
                      )}
                      <div className="col-span-2">
                        <Label className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Drawing</Label>
                        <label className={`mt-1 flex items-center justify-center h-8 rounded text-[10px] border cursor-pointer ${item.drawing_url ? 'text-green-400 border-green-400/30' : isDark ? 'border-white/15 text-white/50 hover:bg-white/5' : 'border-gray-300 text-gray-500 hover:bg-white'}`}>
                          {item.drawing_url ? '✓ uploaded' : <><Upload size={11} className="mr-1" /> Upload</>}
                          <input type="file" accept="image/*,.pdf,.dwg" className="hidden" onChange={(e) => handleDrawingUpload(i, e)} />
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* GST + Totals */}
            <div className={`p-3 rounded-lg ${isDark ? 'bg-white/[0.03] border border-white/5' : 'bg-gray-50 border border-gray-200'}`}>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div>
                  <Label className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>CGST %</Label>
                  <Input className={`mt-1 h-8 text-xs ${inputCls}`} type="number" min="0" max="100" step="0.01" value={form.cgst_rate} onChange={e => setForm(f => ({ ...f, cgst_rate: e.target.value }))} />
                </div>
                <div>
                  <Label className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>SGST %</Label>
                  <Input className={`mt-1 h-8 text-xs ${inputCls}`} type="number" min="0" max="100" step="0.01" value={form.sgst_rate} onChange={e => setForm(f => ({ ...f, sgst_rate: e.target.value }))} />
                </div>
                <div>
                  <Label className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>IGST %</Label>
                  <Input className={`mt-1 h-8 text-xs ${inputCls}`} type="number" min="0" max="100" step="0.01" value={form.igst_rate} onChange={e => setForm(f => ({ ...f, igst_rate: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div className={isDark ? 'text-white/60' : 'text-gray-600'}>
                  <div className="flex justify-between"><span>Subtotal:</span><span>₹{totals.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>
                  {totals.cgst > 0 && <div className="flex justify-between"><span>CGST:</span><span>₹{totals.cgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>}
                  {totals.sgst > 0 && <div className="flex justify-between"><span>SGST:</span><span>₹{totals.sgst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>}
                  {totals.igst > 0 && <div className="flex justify-between"><span>IGST:</span><span>₹{totals.igst.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></div>}
                </div>
                <div className={`text-right ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  <p className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Grand Total</p>
                  <p className="text-xl font-bold">₹{totals.total.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
            </div>

            <div>
              <Label className={isDark ? 'text-white/60' : ''}>Notes</Label>
              <Input className={`mt-1 ${inputCls}`} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes..." />
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className={`flex-1 h-10 ${isDark ? 'border-white/20 text-white hover:bg-white/10' : ''}`}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="flex-1 h-10 bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-testid="so-submit-btn">
                {submitting ? 'Creating...' : 'Create Sales Order'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FlowSalesOrders;
